import { useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

const ACTIVE_PROJECTS_WORKFLOW = {
  COMPLIANCE_DOCUMENTS: [
    {
      user: ["VENDOR"],
      role: [],
      transitions: [ { label: "compliance_save", transitionId: "compliance_save" }, { label: "share_for_review", transitionId: "share_for_review" },],
    },
    {
      user: ["TJ"],
      role: ['PRODUCT CREATOR', 'CATEGORY MANAGER', 'PROJECT MANAGER', 'SYSTEM EXCELLENCE MANAGER'],
      transitions: [
        { label: "request_for_revision", transitionId: "request_for_revision" },
        { label: "review_complete", transitionId: "review_complete" },
        { label: "compliance_save", transitionId: "compliance_save" }
      ],
    },
    {
      user: ["TJ"],
      role: [],
      transitions: [{ label: "compliance_save", transitionId: "compliance_save" }],
    },
  ],
  MAIN_DETAILS: [
    {
      user: ["VENDOR"],
      role: [],
      transitions: [{ label: "main_details_save", transitionId: "main_details_save" }, { label: "share_for_review", transitionId: "share_for_review" }],
    },
    {
      user: ["TJ"],
      role: ['PRODUCT CREATOR', 'CATEGORY MANAGER', 'PROJECT MANAGER', 'SYSTEM EXCELLENCE MANAGER'],
      transitions: [
        { label: "request_for_revision", transitionId: "request_for_revision" },
        { label: "review_complete", transitionId: "review_complete" },
        { label: "main_details_save", transitionId: "main_details_save" }
      ],
    },
    {
      user: ["TJ"],
      role: [],
      transitions: [{ label: "main_details_save", transitionId: "main_details_save" }],
    }
  ],
  RECIPE: [
    {
      user: ["TJ"],
      role: ["NUTRITIONIST", "CATEGORY MANAGER", "PROJECT MANAGER", "SYSTEM EXCELLENCE MANAGER"],
      transitions: [
        { label: "recipe_save", transitionId: "recipe_save" },
        { label: "request_for_revision", transitionId: "request_for_revision" },
        { label: "review_complete", transitionId: "review_complete" },
      ],
    },
    {
      user: ["VENDOR", "TJ"],
      role: [],
      transitions: [{ label: "recipe_save", transitionId: "recipe_save" }],
    },
  ],
  RAW_MATERIALS: [
    {
      user: ["TJ"],
      role: ["NUTRITIONIST", "CATEGORY MANAGER", "PROJECT MANAGER", "SYSTEM EXCELLENCE MANAGER"],
      transitions: [
        { label: "raw_materials_save", transitionId: "raw_materials_save" },
        { label: "request_for_revision", transitionId: "request_for_revision" },
        { label: "review_complete", transitionId: "review_complete" },
      ],
    },
    {
      user: ["VENDOR", "TJ"],
      role: [],
      transitions: [{ label: "raw_materials_save", transitionId: "raw_materials_save" }],
    },
  ],
  ALLERGENS: [
    {
      user: ["VENDOR", "TJ"],
      role: [],
      transitions: [{ label: "allergens_save", transitionId: "allergens_save" }],
    },
  ],
  NUTRITION: [
    {
      user: ["TJ"],
      role: ["NUTRITIONIST"],
      transitions: [
        { label: "nutrition_save", transitionId: "nutrition_save" },
        { label: "request_for_revision", transitionId: "request_for_revision" },
        { label: "review_complete", transitionId: "review_complete" },
      ],
    },
    {
      user: ["VENDOR"],
      role: [],
      transitions: [{ label: "nutrition_save", transitionId: "nutrition_save" }],
    },
  ],
  NUTRITION_DECLARATION: [
    {
      user: ["VENDOR", "TJ"],
      role: [],
      transitions: [
        { label: "nutrition_declaration_save", transitionId: "nutrition_declaration_save" },
      ],
    },
  ],
  FOOD_SAFETY: [
    {
      user: ["TJ"],
      role: ["FOOD SAFETY QA", "BUSINESS ADMIN"],
      transitions: [
        { label: "food_safety_save", transitionId: "food_safety_save" },
        { label: "request_for_revision", transitionId: "request_for_revision" },
        { label: "review_complete", transitionId: "review_complete" },
      ],
    },
    {
      user: ["TJ"],
      role: [],
      transitions: [{ label: "food_safety_save", transitionId: "food_safety_save" }],
    },
    {
      user: ["VENDOR"],
      role: [],
      transitions: [{ label: "food_safety_save", transitionId: "food_safety_save" }],
    },
  ],
  STORAGE: [
    {
      user: ["TJ"],
      role: ["TRAFFIC MANAGER", "BUSINESS ADMIN"],
      transitions: [
        { label: "storage_save", transitionId: "storage_save" },
        { label: "request_for_revision", transitionId: "request_for_revision" },
        { label: "review_complete", transitionId: "review_complete" },
      ],
    },
    {
      user: ["TJ"],
      role: [],
      transitions: [{ label: "storage_save", transitionId: "storage_save" }],
    },
    {
      user: ["VENDOR"],
      role: [],
      transitions: [{ label: "storage_save", transitionId: "storage_save" }],
    },
  ],
  PACKAGING: [
    {
      user: ["VENDOR"],
      role: [],
      transitions: [{ label: "packaging_save", transitionId: "packaging_save" }, { label: "share_for_review", transitionId: "share_for_review" }],
    },
    {
      user: ["TJ"],
      role: ["NUTRITIONIST", "TRAFFIC MANAGER", "CATEGORY MANAGER", "PROJECT MANAGER", "SYSTEM EXCELLENCE MANAGER"],
      transitions: [
        { label: "packaging_save", transitionId: "packaging_save" },
        { label: "request_for_revision", transitionId: "request_for_revision" },
        { label: "review_complete", transitionId: "review_complete" },
      ],
    },
    {
      user: ["TJ"],
      role: [],
      transitions: [{ label: "packaging_save", transitionId: "packaging_save" }],
    },
  ],
};

const useActiveProjectsWorkflow = (type: string) => {
  const { userDetails } = useAuth();

  const retrieveWorkflow = useCallback(() => {
    const userType = userDetails?.type; // "TJ" or "VENDOR"
    const userRoles = userDetails?.roles || []; // Array of roles
    const workflow: Array<{ label: string; transitionId: string }> = [];

    const workflowData = ACTIVE_PROJECTS_WORKFLOW?.[type as keyof typeof ACTIVE_PROJECTS_WORKFLOW];

    if (!workflowData || !Array.isArray(workflowData)) {
      return workflow;
    }

    // Business Admin: see all transitions (both TJ and VENDOR actions)
    const isBusinessAdmin = userRoles.some(role => role.rolename === 'BUSINESS ADMIN');
    if (isBusinessAdmin) {
      const allTransitions = workflowData
        .flatMap(item => item.transitions || [])
        .filter(Boolean);
      workflow.push(...allTransitions);
      return workflow;
    }

    // Helper function to check if user has a specific role
    const hasRole = (roleName: string) => {
      return userRoles.some(role => role.rolename === roleName);
    };

    // Find matching workflow based on user type and role
    const matchingWorkflow = workflowData.find(item => {
      // Match user type - now checking if userType is in the user array
      if (!userType || !item.user.includes(userType)) {
        return false;
      }
      // If roles are specified, check if user has any of those roles
      if (item.role && item.role.length > 0) {
        const hasAnyRequiredRole = item.role.some(requiredRole => hasRole(requiredRole));
        if (!hasAnyRequiredRole) {
          return false;
        }
      }
      return true;
    });

    if (matchingWorkflow?.transitions) {
      workflow.push(...matchingWorkflow.transitions);
    }

    const userRoleNames = userRoles.map(role => role.rolename);
    const requiredRoles = matchingWorkflow?.role || [];
    console.log(userType, "userType", userRoleNames, "userRoles", requiredRoles, "requiredRoles", workflow, "workflow");
    return workflow;
  }, [userDetails?.type, userDetails?.roles]);
  return retrieveWorkflow;
};

export default useActiveProjectsWorkflow;
